import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Field, FieldArray, FieldProps, Form, FormikProvider, useFormik } from 'formik';
import orderBy from 'lodash/orderBy';
import * as Yup from 'yup';
import { FieldWrapper, Input, ModalFactory } from 'components';
import { ItemCreatableSelect } from 'components/ItemCreatableSelect';
import { selectPOSCategories } from 'store/Collections';
import { ICalculatorRules, ICategoryGroup } from 'types';
import { normalizeString, stripSpaces, toTitleCase } from 'utils/strings';
import { SectionTitle } from '../../../Settings/components';
import { AddButton, StyledIconButton } from '../../styles';
import { ButtonContainer, Column, ModalContent, ModalInfo, SalesGroupFormContainer, StyledButton, StyledDiv, TipCalculatorFieldsContainer, TipCalculatorRow } from './styles';

const SALES_GROUP_ITEM_LIMIT = 50;
const nameRegex = /^[a-zA-Z0-9\s]*$/;
const nameRegexErrorMessage = 'Valid characters include: A-Z, a-z, 0-9, and white space';
const restrictedNames = ['tip', 'tips', 'autograts'];

interface ICreateSalesGroup {
  hideModal: () => void;
  calculatorRules?: ICalculatorRules;
  onConfirm: (action: string, vals: ICategoryGroup, onSuccess?: () => void) => void;
  isLoading?: boolean;
  selectedGroup?: ICategoryGroup;
}

export const CreateSalesGroup = ({ hideModal, calculatorRules, onConfirm, isLoading, selectedGroup }: ICreateSalesGroup) => {
  const posCategories = useSelector(selectPOSCategories());

  const posCategoriesSelectorOptions = useMemo(
    () =>
      orderBy(posCategories.categories, [(cat) => cat.name.toLowerCase()], ['asc']).map((category) => ({
        value: category.name,
        label: toTitleCase(category.name),
      })),
    [posCategories]
  );

  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .trim()
      .required('Name is required')
      .max(25, 'Name must be 25 characters or less')
      .matches(nameRegex, nameRegexErrorMessage)
      .test('isRestricted', 'This name is not allowed', (name = '') => {
        const normalizedName = normalizeString(name);
        return !restrictedNames.includes(normalizedName);
      })
      .test('isUniqueByGroup', 'This name is already in use by a sales group', (name = '') => {
        const normalizedName = normalizeString(name);
        return !calculatorRules?.categoryGroups.some((group) => normalizeString(group.name) === normalizedName) || (selectedGroup && normalizedName === normalizeString(selectedGroup.name));
      })
      .test('isUniqueByCategory', 'This name is already in use by a sales category', (name = '') => {
        const normalizedName = normalizeString(name);
        return !posCategories?.categories.some((cat) => normalizeString(cat.name) === normalizedName);
      }),
    categories: Yup.array().of(Yup.string().required()),
  });

  const handleSubmit = (values: ICategoryGroup) => {
    onConfirm(selectedGroup ? 'edit' : 'create', { ...values, name: stripSpaces(values.name) }, hideModal);
  };

  const formik = useFormik({
    initialValues: {
      name: selectedGroup?.name || '',
      categories: selectedGroup?.categories || [''],
    },
    onSubmit: handleSubmit,
    validationSchema,
  });

  const { dirty, isValid, setFieldTouched, setFieldValue, values } = formik;

  return (
    <ModalFactory title={`${selectedGroup ? 'Edit' : 'Create'} Sales Group`} onClose={hideModal} isActive maxWidth={600} closeOnOutSideClick={false} hideCloseButton>
      <ModalContent>
        <ModalInfo size="small">
          {selectedGroup
            ? 'Add or remove sales categories to your sales group. If you change the name of the sales group then it will save as a new sales group.'
            : 'Give your Sales Group a unique name and specify which sales categories belong to the group. Sales categories can belong to multiple groups.'}
        </ModalInfo>
        <br />
        <FormikProvider value={formik}>
          <Form>
            <SalesGroupFormContainer>
              <Column>
                <StyledDiv>
                  <SectionTitle>Group Name</SectionTitle>
                </StyledDiv>
                <Field name="name">
                  {({ field, meta }: FieldProps) => (
                    <FieldWrapper message={meta.error && meta.touched ? meta.error : ''} hasError={meta.error && meta.touched}>
                      <Input data-cy="name-input" id="name" type="text" placeholder="Enter name here" {...field} maxLength={30} hasError={meta.error && meta.touched} />
                    </FieldWrapper>
                  )}
                </Field>
              </Column>
              <Column>
                <StyledDiv>
                  <SectionTitle>Sales Categories to be Grouped</SectionTitle>
                </StyledDiv>
                <FieldArray
                  name="categories"
                  render={(categoriesArrayHelpers) => (
                    <TipCalculatorFieldsContainer>
                      {values.categories.map((cat, idx) => (
                        <TipCalculatorRow key={`category_${cat || idx}`}>
                          <Field name={`categories.${idx}`} className="selector">
                            {({ field, meta }: FieldProps) => (
                              <ItemCreatableSelect
                                {...field}
                                defaultValue={field.value}
                                placeholder="Select"
                                createNewLabel="Add {search} as new sales category"
                                onChange={(category) => {
                                  if (category) {
                                    if (!meta.touched) setFieldTouched(`categories.${idx}`);
                                    setFieldValue(`categories.${idx}`, category.value);
                                  }
                                }}
                                options={posCategoriesSelectorOptions}
                                filterOptions={values.categories.filter((cat) => cat && cat !== field.value)}
                                dataCy="category-creatable-selector"
                              />
                            )}
                          </Field>
                          {values.categories.length > 1 && <StyledIconButton label="Remove" onClick={() => categoriesArrayHelpers.remove(idx)} appearance="white" size="small" icon="Close" data-cy="remove-category-button" $noMargin />}
                        </TipCalculatorRow>
                      ))}
                      {values.categories.length < SALES_GROUP_ITEM_LIMIT && <AddButton appearance="white" icon="Plus" label="Sales Category" onClick={() => categoriesArrayHelpers.push('')} data-cy="add-category-button" />}
                    </TipCalculatorFieldsContainer>
                  )}
                />
              </Column>
            </SalesGroupFormContainer>
            <ButtonContainer tipShare>
              <StyledButton label="Cancel" data-cy="cancel-button" appearance="white" onClick={hideModal} />
              <StyledButton type="submit" label="Save" data-cy="save-button" isProcessing={isLoading} disabled={isLoading || !dirty || !isValid} />
            </ButtonContainer>
          </Form>
        </FormikProvider>
      </ModalContent>
    </ModalFactory>
  );
};
